import { useState, useEffect, useContext } from "react";
import { Box, Card, Snackbar, Alert, Typography } from "@mui/material";
import styles from './Login.module.scss';
import TitleSubtitle from "../../components/common/formControls/titleSubtitle/TitleSubtitle";
import { useTranslation } from "react-i18next";
import InputTextField from "../../components/common/formControls/textField/InputTextField";
import InputPasswordTextField from "../../components/common/formControls/textField/InputPasswordTextField";
import AppLogo from "../../components/common/appLogo/AppLogo";
import { LoginRequest, ProcessResult } from "../../common/Interfaces";
import NormalButton from "../../components/common/formControls/buttons/NormalButton";
import { requestLogin } from "../../common/requests/LoginRequest";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";

export default function Login (){
    const {authenticated, setAuthenticated} = useContext(AuthContext)
    const {t} = useTranslation();
    const navigate = useNavigate();
    const [loginRequest, setLoginRequest] = useState<LoginRequest>({});
    const [userNameError, setUserNameError] = useState<string|undefined>(undefined);
    const [passwordError, setPasswordError] = useState<string|undefined>(undefined);
    const [loginErrorMessage, setLoginErrorMessage] = useState<string|undefined>(undefined);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [successLogin, setSuccessLogin] = useState<boolean>(false);

    useEffect(() => {
        if (!!successLogin){
            setIsLoading(false);
            setAuthenticated(true)
            navigate("/");
        }
    }, [successLogin, navigate])

    const handleLoginClick = async () => {
        setUserNameError(undefined);
        setPasswordError(undefined);
        setLoginErrorMessage(undefined);
        setSuccessLogin(false);

        if(validateCredentials(true)){
            setIsLoading(true);
            try{
                const loginResult:ProcessResult = await requestLogin(loginRequest);

                if (!!loginResult.Ok){
                    setSuccessLogin(true);        
                    return;
                }
                
                setLoginErrorMessage(loginResult.data ?? t("INVALIDCREDENTIALS"));
            }catch (error){
                setLoginErrorMessage(`${t("UNEXPECTEDERROR")}: ${JSON.stringify(error)}`);
            }
            finally{
                setIsLoading(false);
            }
        }
    }

    const handleValueChange = (e: any) => {
        setLoginRequest({
            ...loginRequest,
            [e.target.name]: e.target.value,
        })
    }

    const validateCredentials = (reportError: boolean): boolean => {
        if (!loginRequest?.UserName || loginRequest.UserName.trim().length === 0){
            !!reportError && setUserNameError(t("EMPTYFIELDERROR"));
            return false;
        }

        if (!loginRequest?.Password || loginRequest.Password.trim().length === 0){
            !!reportError && setPasswordError(t("EMPTYFIELDERROR"));
            return false;
        }

        return true;
    }

    const keyPressValidation = (e: any) => {
        if (e.key === 'Enter') {
            handleLoginClick();
        }
      };

    return(<Box className={styles.container}>
        <Snackbar
            open={!!loginErrorMessage}
            anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
            autoHideDuration={3000}
            onClose={() => {
                setLoginErrorMessage(undefined);
            }}
        >
            <Alert severity="error" className={styles.alertContainer}>
                {loginErrorMessage}
            </Alert>
        </Snackbar>

        <Card className={styles.cardContainer}>
            <AppLogo/>
            <TitleSubtitle
                title={t("LOGINTITLE")}
                subTitle={t("LOGINMESSAGE")}
            />
            <InputTextField id={"UserName"} 
                            label={t("USERNAME")} 
                            value={loginRequest.UserName ?? ''} 
                            errorMessage={userNameError}
                            handleValueChange={handleValueChange}
                            onKeyPress={keyPressValidation} />

            <InputPasswordTextField id={"Password"} 
                                    label={t("PASSWORD")} 
                                    value={loginRequest.Password ?? ''} 
                                    errorMessage={passwordError}
                                    handleValueChange={handleValueChange}
                                    onKeyPress={keyPressValidation} />

            <Box className={styles.buttonLayout}>
                <NormalButton id={"LoginButton"} 
                              label={t("LOGIN")} 
                              disabled={!!isLoading}
                              onclickHandler={handleLoginClick} />
            </Box>
            <Box className={styles.versionContainer}>
                <Typography>{t("VERSION")} {t("APPVERSION")}</Typography>
            </Box>
        </Card>
    </Box>);
}